import type { DisciplineId, SkillGroupId, TrendStatus } from "./schema";
import type { FilterState, RadarEntity } from "./provider";

const GROUPS: SkillGroupId[] = [
  "scientific-lab",
  "digital-data",
  "research",
  "green-sustainability",
  "entrepreneurial-innovation",
  "transferable-professional",
];

export function matchesDiscipline(entity: RadarEntity, discipline: FilterState["discipline"]): boolean {
  if (discipline === "all") return true;
  return entity.disciplineIds.includes(discipline);
}

export function matchesTrend(entity: RadarEntity, trend: FilterState["trend"]): boolean {
  if (trend === "all") return true;
  return entity.trendStatus === trend;
}

export function matchesGeography(entity: RadarEntity, geography: FilterState["geography"]): boolean {
  if (geography === "all") return true;
  return entity.geography.includes(geography);
}

export function searchBlob(entity: RadarEntity): string {
  return [entity.name, entity.description, ...entity.aliases].join(" ").toLowerCase();
}

export function matchesQuery(entity: RadarEntity, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return searchBlob(entity).includes(needle);
}

export function matchesGroup(entity: RadarEntity, group: FilterState["group"]): boolean {
  if (group === "all") return true;
  return entity.groupId === group;
}

export function filterRadarEntities(entities: RadarEntity[], filters: FilterState): RadarEntity[] {
  return entities.filter(
    (e) =>
      matchesDiscipline(e, filters.discipline) &&
      matchesTrend(e, filters.trend) &&
      matchesGeography(e, filters.geography) &&
      matchesGroup(e, filters.group) &&
      matchesQuery(e, filters.q),
  );
}

export function mostConnected(entities: RadarEntity[], limit = 3): RadarEntity[] {
  return [...entities]
    .map((e) => ({
      e,
      degree:
        e.relatedSkillIds.length +
        e.relatedCareerIds.length +
        e.relatedTechnologyIds.length +
        e.relatedSectorIds.length,
    }))
    .sort((a, b) => b.degree - a.degree)
    .slice(0, limit)
    .filter((x) => x.degree > 0)
    .map((x) => x.e);
}

export function connectedIds(entity: RadarEntity, pool: RadarEntity[] = []): Set<string> {
  const ids = new Set([
    entity.id,
    ...entity.relatedSkillIds,
    ...entity.relatedCareerIds,
    ...entity.relatedTechnologyIds,
    ...entity.relatedSectorIds,
  ]);
  for (const other of pool) {
    if (other.id === entity.id) continue;
    const sameCareer = other.relatedCareerIds.some((id) => entity.relatedCareerIds.includes(id));
    const sameTech = other.relatedTechnologyIds.some((id) => entity.relatedTechnologyIds.includes(id));
    const sameSkill = other.relatedSkillIds.some((id) => entity.relatedSkillIds.includes(id) || id === entity.id);
    if (sameCareer || sameTech || sameSkill || entity.relatedSkillIds.includes(other.id)) ids.add(other.id);
  }
  return ids;
}

/** Other skills in `pool` linked by shared careers or technologies (map connections). */
export function connectedPeerIds(entity: RadarEntity, pool: RadarEntity[]): string[] {
  const connected = connectedIds(entity, pool);
  return pool.filter((other) => other.id !== entity.id && other.type === "skill" && connected.has(other.id)).map((other) => other.id);
}

/** Catalog-declared related skills, or peer skills for skill rows (skills have no relatedSkillIds). */
export function relatedSkillsCount(entity: RadarEntity, skillPool: RadarEntity[]): number {
  if (entity.type === "skill") return connectedPeerIds(entity, skillPool).length;
  return entity.relatedSkillIds.length;
}

export function defaultFilters(): FilterState {
  return {
    discipline: "all",
    view: "skill",
    trend: "all",
    geography: "all",
    group: "scientific-lab",
    q: "",
    connections: false,
  };
}

export function parseFilters(params: URLSearchParams): FilterState {
  const filters = defaultFilters();
  const discipline = params.get("discipline");
  const view = params.get("type") ?? params.get("view");
  const trend = params.get("trend");
  const geography = params.get("geo") ?? params.get("geography");
  const q = params.get("q") ?? "";
  const entity = params.get("entity") ?? params.get("skill") ?? undefined;
  const connections = params.get("connections") === "1";
  const group = params.get("group");
  if (discipline === "biology" || discipline === "chemistry" || discipline === "interdisciplinary") {
    filters.discipline = discipline;
  }
  if (view === "technology" || view === "skill" || view === "career" || view === "sector") {
    filters.view = view;
  }
  if (view && view !== "skill") filters.group = "all";
  if (group === "all" || GROUPS.includes(group as SkillGroupId)) {
    filters.group = group as FilterState["group"];
  }
  if (trend === "emerging" || trend === "growing" || trend === "established" || trend === "transforming") {
    filters.trend = trend as TrendStatus;
  }
  if (geography === "europe" || geography === "ukraine" || geography === "all") {
    filters.geography = geography;
  }
  filters.q = q;
  filters.entity = entity;
  filters.connections = connections;
  return filters;
}

export function filtersToParams(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.discipline !== "all") params.set("discipline", filters.discipline);
  if (filters.view !== "technology") params.set("type", filters.view);
  if (filters.trend !== "all") params.set("trend", filters.trend);
  if (filters.geography !== "all") params.set("geo", filters.geography);
  if (filters.view === "skill") params.set("group", filters.group);
  else if (filters.group !== "all") params.set("group", filters.group);
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.entity) params.set("entity", filters.entity);
  if (filters.connections) params.set("connections", "1");
  return params;
}

export function disciplineLabel(id: DisciplineId | "all"): string {
  if (id === "all") return "All";
  if (id === "biology") return "Biology";
  if (id === "chemistry") return "Chemistry";
  return "Interdisciplinary";
}
